'use client';

import { DesktopFlowHeader } from './desktop-flow-header';
import Link from 'next/link';
import { Logo } from './logo';
import { MobileFlowHeader } from './mobile-flow-header';
import { SidebarSteps } from './sidebar-steps';
// BS-2161: sonner's `<Toaster />` (~68 KB raw) was previously imported
// statically here, pulling the whole sonner module into the shared layout
// chunk on every route - including `/`, `/_not-found`, and the home route
// that never render a toast. Lazy-loading defers the chunk until after
// hydration. `toast.*` calls elsewhere stay synchronous (they no-op until
// the Toaster mounts, which happens within one tick of hydration).
import { ToasterLazy } from './toaster-lazy';
import { useCurrentFlowStep } from '~/lib/flow-steps';

const NMLS_URL =
  'https://uscode.house.gov/view.xhtml?req=granuleid:USC-prelim-title12-chapter51&saved=%7CKHRpdGxlOjEyIHNlY3Rpb246NTEwMSBlZGl0aW9uOnByZWxpbSkgT1IgKGdyYW51bGVpZDpVU0MtcHJlbGltLXRpdGxlMTItc2VjdGlvbjUxMDEp%7CdHJlZXNvcnQ%3D%7C%7C0%7Cfalse%7Cprelim&edition=prelim';

interface MainLayoutProps {
  children: React.ReactNode;
}

export function MainLayout({ children }: MainLayoutProps) {
  const inFlow = useCurrentFlowStep() !== null;

  return (
    <div className="min-h-dvh bg-brand-navy lg:h-dvh lg:overflow-hidden">
      <div className="flex min-h-dvh flex-col lg:h-full lg:flex-row">
        <aside className="text-white max-lg:hidden lg:flex lg:h-dvh lg:w-80 lg:shrink-0 lg:flex-col lg:px-8 lg:py-10 xl:w-96">
          <Link href="/" aria-label="Homepage" className="inline-flex">
            <Logo size="sm" className="w-28" />
          </Link>

          <div className="mt-8 flex flex-col gap-y-3">
            <h2 className="font-serif text-2xl font-medium tracking-tight text-balance">
              A loan to back what&apos;s next
            </h2>
            {!inFlow && (
              <p className="max-w-sm text-sm leading-relaxed text-pretty text-white/60">
                Rates, terms, and a decision without the paperwork marathon.
              </p>
            )}
          </div>

          {inFlow && <SidebarSteps />}

          <div className="mt-auto flex flex-col gap-y-10">
            <div className="flex flex-col gap-y-1 font-mono text-xs font-semibold tracking-[0.12em] text-[oklch(0.71_0.066_222.1)] uppercase">
              <span>Federally insured by the NCUA</span>
              <span>Equal Housing Opportunity</span>
              <a
                className="w-fit underline decoration-current/40 underline-offset-4 transition-colors hover:text-white hover:decoration-current"
                href={NMLS_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                NMLS #489291
              </a>
            </div>
            <Logo variant="mark" size="sm" className="size-6" />
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col bg-surface-1 lg:m-2 lg:overflow-hidden lg:rounded-xl lg:shadow-sm">
          <div className="max-lg:hidden">
            <DesktopFlowHeader />
          </div>

          <div className="flex flex-1 flex-col lg:overflow-y-auto lg:overscroll-contain">
            <MobileFlowHeader />

            <div
              aria-hidden="true"
              className="pointer-events-none sticky top-0 z-10 h-6 shrink-0 bg-linear-to-b from-surface-1 to-transparent max-lg:hidden"
            />

            <main className="flex flex-1 flex-col px-4 py-6 sm:px-10 sm:py-10 lg:px-16 lg:py-12">
              {children}
            </main>

            <footer className="flex flex-col items-center gap-y-4 px-4 py-6 lg:hidden">
              <div className="flex flex-col items-center gap-y-1 text-center font-mono text-xs font-semibold tracking-[0.12em] text-fg-faint uppercase">
                <span>Federally insured by the NCUA</span>
                <span>Equal Housing Opportunity</span>
                <a
                  className="underline decoration-current/40 underline-offset-4 transition-colors hover:text-fg hover:decoration-current"
                  href={NMLS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  NMLS #489291
                </a>
              </div>
              <Logo variant="mark" size="sm" className="size-7" />
            </footer>
          </div>
        </div>
      </div>

      <ToasterLazy />
    </div>
  );
}
